import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  UilFileContract,
  UilCheckCircle,
  UilAngleDown,
  UilAngleUp,
  UilShieldCheck,
  UilCalendarAlt,
  UilEnvelope,
} from '@iconscout/react-unicons';
import Faby from '../assets/Icons/faby.webp';
import FooterPage from './FooterPage';

const sectionsData = [
  {
    title: 'General Terms',
    content: `Fabiana Fabulous Cleaning Services LLC ("Company") may amend these terms at any time without notice. By booking or using our services, you agree to be bound by the current terms.`,
    icon: <UilFileContract size={24} />,
  },
  {
    title: 'COVID-19 Policy',
    content: `Customers must disclose any COVID-19 symptoms or exposure prior to booking. Failure to do so may result in cancellation.`,
    icon: <UilShieldCheck size={24} />,
  },
  {
    title: 'Refunds',
    content: `We do not offer refunds. Complaints made within 24 hours of service may be eligible for a free re-clean.`,
    icon: <UilCheckCircle size={24} />,
  },
  {
    title: 'Receipts After Cleaning',
    content: `Invoices are sent upon request.`,
    icon: <UilFileContract size={24} />,
  },
  {
    title: 'No Terms or Contract',
    content: `There is no fixed term. Cancellations require 24 hours' notice; otherwise, a $99 fee applies.`,
    icon: <UilCalendarAlt size={24} />,
  },
  {
    title: 'Move-Out Cleaning',
    content: `Homes must be empty for move-out cleaning. Extra charges may apply for items left in the fridge or cabinets.`,
    icon: <UilCheckCircle size={24} />,
  },
  {
    title: 'Hourly Services',
    content: `Hourly services are billed at the current rate. Changes to booking type after service has begun are not allowed.`,
    icon: <UilCalendarAlt size={24} />,
  },
  {
    title: 'Cleaning Approvals',
    content: `Customers may confirm satisfaction via voice, email, or text. Requests for re-clean may be denied if not acknowledged within 24 hours.`,
    icon: <UilCheckCircle size={24} />,
  },
  {
    title: 'Teams and Conduct',
    content: `Cleaners work in teams based on job size and are instructed to respect customer property and maintain professionalism.`,
    icon: <UilShieldCheck size={24} />,
  },
  {
    title: 'Arrival Time',
    content: `Scheduled arrival times have a one-hour window to accommodate delays.`,
    icon: <UilCalendarAlt size={24} />,
  },
  {
    title: 'Scheduling and Pricing Adjustments',
    content: `Prices for recurring services may vary based on frequency. Rescheduling may result in a price adjustment.`,
    icon: <UilCalendarAlt size={24} />,
  },
  {
    title: 'Working at Heights',
    content: `Cleaners will not work at heights above two ladder rungs for safety reasons.`,
    icon: <UilShieldCheck size={24} />,
  },
  {
    title: 'Payment Policy and Cancellations',
    content: `Payment is due after service completion. Accepted methods include Cash App, Zelle, Venmo, PayPal, and cash.`,
    icon: <UilFileContract size={24} />,
  },
  {
    title: 'Late Cancellations and Rescheduling',
    content: `Late cancellations incur a half price of the house fee. Rescheduling is free with more than 24 hours' notice.`,
    icon: <UilCalendarAlt size={24} />,
  },
  {
    title: 'Lockouts and Inaccessibility',
    content: `If a cleaner cannot access the property, a $79 fee will apply. Additional travel fees may apply for homes outside city limits.`,
    icon: <UilCheckCircle size={24} />,
  },
  {
    title: 'Accidents and Theft',
    content: `The client is responsible for securing valuables. Any damages must be reported within 24 hours of service.`,
    icon: <UilShieldCheck size={24} />,
  },
  {
    title: 'Pets',
    content: `Customers with pets should secure them and pick up after them. Cleaners do not clean litter boxes or animal waste.`,
    icon: <UilCheckCircle size={24} />,
  },
  {
    title: 'Health and Safety',
    content: `Fabiana Fabulous Cleaning Services LLC reserves the right to refuse work that could compromise health and safety.`,
    icon: <UilShieldCheck size={24} />,
  },
  {
    title: 'Quality Control',
    content: `Our quality control includes inspections and client contact to ensure satisfaction.`,
    icon: <UilCheckCircle size={24} />,
  },
  {
    title: 'Before and After Photos',
    content: `We take before and after photos for quality assurance, which may be used for marketing purposes without revealing customer information.`,
    icon: <UilFileContract size={24} />,
  },
  {
    title: 'Items We Cannot Clean',
    content: `Certain items, such as body fluids, pet accidents, or seasonal insect infestations, are outside our scope of cleaning services.`,
    icon: <UilCheckCircle size={24} />,
  },
];

function Section({ title, content, icon }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mb-4"
    >
      <div
        className="flex justify-between items-center cursor-pointer bg-white border-2 border-gray-200 hover:border-blue-500 p-6 rounded-xl transition-all shadow-sm hover:shadow-md"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-3">
          <div className="text-blue-600">{icon}</div>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        </div>
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-blue-600"
        >
          {open ? <UilAngleUp size={28} /> : <UilAngleDown size={28} />}
        </motion.div>
      </div>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="p-6 bg-gray-50 rounded-b-xl border-2 border-t-0 border-gray-200 mt-[-8px]">
          <p className="text-gray-700 text-lg leading-relaxed">{content}</p>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function TermsAndConditions() {
  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>Terms & Conditions - Fabiana Fabulous Cleaning Services</title>
        <meta name="description" content="Terms and Conditions for Fabiana Fabulous Cleaning Services LLC. Read our policies, cancellation terms, and service agreements." />
        <link rel="canonical" href="https://fabycleaners.com/terms-and-conditions" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative min-h-[60vh] flex items-center bg-cover bg-center bg-fixed pt-20"
        style={{ backgroundImage: `url(${Faby})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-purple-900/85 to-blue-800/90" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6"
            >
              <UilFileContract size={48} className="text-yellow-400" />
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Terms & <span className="text-yellow-400">Conditions</span>
            </h1>

            <p className="text-xl md:text-2xl mb-4 text-gray-100 max-w-3xl mx-auto">
              Please read our service terms carefully
            </p>
            <p className="text-lg text-gray-300">
              Last updated: January 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-20">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 bg-white rounded-2xl p-8 shadow-lg"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
              <UilCheckCircle size={24} />
            </div>
            <h2 className="text-3xl font-bold">Agreement to Terms</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            Welcome to Fabiana Fabulous Cleaning Services LLC. By using our services, you agree to comply with
            these terms and conditions. If you disagree with any part of these terms, please do not use our site or services.
            These terms govern your relationship with us and apply to all services we provide.
          </p>
        </motion.section>

        {/* Accordion Sections */}
        <div className="space-y-4">
          {sectionsData.map(({ title, content, icon }) => (
            <Section key={title} title={title} content={content} icon={icon} />
          ))}
        </div>

        {/* Bottom Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 bg-blue-50 border-l-4 border-blue-600 rounded-lg p-6"
        >
          <div className="flex items-start gap-3">
            <UilCheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={24} />
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Important Notice</h3>
              <p className="text-gray-700 leading-relaxed">
                By booking our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
                We reserve the right to modify these terms at any time, and continued use of our services constitutes acceptance of any changes.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white shadow-xl"
        >
          <UilFileContract size={48} className="mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-3">Questions About Our Terms?</h3>
          <p className="text-lg text-gray-100 mb-6">
            If you have any questions or need clarification on any of these terms, we're here to help.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white hover:bg-gray-100 text-blue-600 font-bold rounded-lg shadow-lg transition-all"
          >
            <UilEnvelope size={24} />
            Contact Us
          </a>
        </motion.div>
      </div>

      <FooterPage />
    </div>
  );
}
